// utils/audio/useMicVolume.js
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Mic volume monitor with noise gate and attack/release smoothing.
 * Returns { volume (0..1), levelDb, permission, error, start, stop }.
 */
export function useMicVolume(opts) {
    const {
        autostart = true,
        gateDb = -55, // raise to -50 if noisy room, lower to -60 for more sensitivity
        attackMs = 60,
        releaseMs = 250,
        stream = null, // pass an existing MediaStream to avoid extra permission prompts
    } = opts || {};

    const [volume, setVolume] = useState(0);
    const [levelDb, setLevelDb] = useState(-100);
    const [permission, setPermission] = useState("unknown"); // "unknown" | "granted" | "denied"
    const [error, setError] = useState(null);

    const ctxRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);
    const dataRef = useRef(null);
    const startedRef = useRef(false);
    const streamRef = useRef(stream);

    const coeffs = useMemo(() => {
        // convert ms→per-frame (assuming ~60fps)
        const framesPerMs = 60 / 1000;
        const atk = Math.exp(-1 / (attackMs * framesPerMs));
        const rel = Math.exp(-1 / (releaseMs * framesPerMs));
        return { atk, rel };
    }, [attackMs, releaseMs]);

    useEffect(() => {
        if (!autostart) return;
        start();
        return stop; // cleanup
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autostart, gateDb, coeffs.atk, coeffs.rel, stream]);

    async function ensureNodes() {
        if (!ctxRef.current) {
            const AC = window.AudioContext || window.webkitAudioContext;
            ctxRef.current = new AC();
        }
        const ctx = ctxRef.current;

        if (!streamRef.current) {
            try {
                const s = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                });
                streamRef.current = s;
                setPermission("granted");
            } catch (e) {
                setPermission("denied");
                setError(e?.message || "Microphone permission denied");
                throw e;
            }
        }

        if (!analyserRef.current) {
            const source = ctx.createMediaStreamSource(streamRef.current);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 1024;
            analyser.smoothingTimeConstant = 0; // we smooth manually
            source.connect(analyser);
            analyserRef.current = analyser;
            dataRef.current = new Float32Array(analyser.fftSize);
        }
    }

    function loop() {
        const analyser = analyserRef.current;
        const buf = dataRef.current;
        analyser.getFloatTimeDomainData(buf);

        // RMS
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
            const v = buf[i];
            sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length) || 0;

        // dBFS approx
        const db = rms > 0 ? 20 * Math.log10(rms) : -100;

        // Gate + normalize dB range [gateDb..0] → [0..1]
        const gatedDb = Math.max(db, gateDb);
        const norm = Math.min(
            1,
            Math.max(0, (gatedDb - gateDb) / (0 - gateDb))
        );

        setVolume((prev) => {
            const target = norm;
            const rising = target > prev;
            const k = rising ? coeffs.atk : coeffs.rel;
            return prev * k + target * (1 - k);
        });
        setLevelDb(db);

        rafRef.current = requestAnimationFrame(loop);
    }

    async function start() {
        if (startedRef.current) return;
        try {
            await ensureNodes();
            if (ctxRef.current?.state === "suspended") {
                await ctxRef.current.resume();
            }
            startedRef.current = true;
            rafRef.current = requestAnimationFrame(loop);
        } catch {
            // error already set
        }
    }

    function stop() {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        startedRef.current = false;
        // Keep nodes/stream alive for reuse; uncomment to fully teardown:
        // analyserRef.current?.disconnect();
        // analyserRef.current = null;
        // ctxRef.current?.close();
        // ctxRef.current = null;
        // streamRef.current?.getTracks().forEach(t => t.stop());
        // streamRef.current = null;
    }

    return {
        volume,
        levelDb,
        permission,
        error,
        start,
        stop,
        stream: streamRef.current,
    };
}
